import { useEffect, useState } from 'react';
import 'twin.macro';
import { networkMeter } from '../../services/network-meter';

// Development-only network usage meter panel. Mounted by App.jsx only when
// isNetworkMeterEnabled(). Hidden by default: Ctrl+Alt+M or ?netmeter=1 shows
// it, and the choice sticks for the dev session. Everything it shows comes from
// services/network-meter; it measures nothing itself.

const STORAGE_KEY = 'network-meter-panel';

const readInitialVisibility = () => {
  try {
    if (new URLSearchParams(window.location.search).get('netmeter') === '1') {
      return true;
    }
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch (_error) {
    return false;
  }
};

const writeVisibility = visible => {
  try {
    localStorage.setItem(STORAGE_KEY, visible ? '1' : '0');
  } catch (_error) {
    // In-memory toggle still works.
  }
};

const formatBytes = bytes => {
  if (bytes === null || bytes === undefined) {
    return 'n/a';
  }
  if (bytes >= 1_000_000) {
    return `${(bytes / 1_000_000).toFixed(2)} MB`;
  }
  if (bytes >= 1000) {
    return `${(bytes / 1000).toFixed(1)} kB`;
  }
  return `${bytes} B`;
};

const formatMbps = value => (value === null || value === undefined ? 'n/a' : value.toFixed(3));

const formatElapsed = ms => {
  const total = Math.floor(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = String(total % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
};

const LEVEL_SHORT = {
  'A-application-payload': 'A',
  'B-browser-reported': 'B',
};

const Button = ({ onClick, children, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    tw="rounded bg-white/15 px-2 py-0.5 text-[11px] text-white hover:bg-white/25 disabled:opacity-40"
  >
    {children}
  </button>
);

export default function NetworkMeterPanel() {
  const [visible, setVisible] = useState(readInitialVisibility);
  const [report, setReport] = useState(null);
  const [scenario, setScenario] = useState('active-session');

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.ctrlKey && event.altKey && event.code === 'KeyM') {
        setVisible(current => {
          writeVisibility(!current);
          return !current;
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (!visible) {
      return undefined;
    }
    const refresh = () => setReport(networkMeter.report());
    refresh();
    const unsubscribe = networkMeter.subscribe(refresh);
    return unsubscribe;
  }, [visible]);

  if (!visible || !report) {
    return null;
  }

  const running = report.meta.running;
  const levelA = report.totalsByLevel['A-application-payload'];
  const levelB = report.totalsByLevel['B-browser-reported'];
  const rows = report.rows.filter(row => row.includedInTotals || row.partOf);
  const gaps = report.coverage.filter(item => item.status !== 'measured');

  return (
    <div tw="fixed bottom-2 right-2 z-[99998] max-h-[85vh] w-[420px] max-w-[calc(100vw-1rem)] overflow-auto rounded-lg bg-black/85 p-3 font-mono text-[11px] leading-4 text-white">
      <div tw="mb-2 flex items-center justify-between">
        <strong>Network meter (dev only)</strong>
        <button
          type="button"
          tw="text-white/70"
          onClick={() => {
            writeVisibility(false);
            setVisible(false);
          }}
        >
          ×
        </button>
      </div>

      <div tw="mb-2 flex flex-wrap items-center gap-1">
        <input
          value={scenario}
          onChange={event => setScenario(event.target.value)}
          disabled={running}
          tw="w-[130px] rounded bg-white/10 px-1 text-white"
          aria-label="Scenario label"
        />
        <Button disabled={running} onClick={() => networkMeter.start({ scenario })}>
          Start
        </Button>
        <Button disabled={!running} onClick={() => void networkMeter.stop()}>
          Stop
        </Button>
        <Button onClick={() => networkMeter.reset()}>Reset</Button>
        <Button onClick={() => networkMeter.downloadJson()}>JSON</Button>
        <Button onClick={() => networkMeter.downloadCsv()}>CSV</Button>
      </div>

      <div tw="mb-2">
        <div>
          {running ? 'RUNNING' : report.meta.startedAtIso ? 'STOPPED' : 'IDLE'} · elapsed{' '}
          {formatElapsed(report.meta.durationMs)} · stage {report.meta.currentStage}
          {report.meta.scenario ? ` · ${report.meta.scenario}` : ''}
        </div>
        <div>
          A payload: ↑ {formatBytes(levelA.uploadBytes)} ↓ {formatBytes(levelA.downloadBytes)}
        </div>
        <div>
          B browser: ↑ {formatBytes(levelB.uploadBytes)} ↓ {formatBytes(levelB.downloadBytes)}
          {levelB.rowsWithUnknownUpload
            ? ` (upload unknown for ${levelB.rowsWithUnknownUpload} rows)`
            : ''}
        </div>
        <div tw="text-white/60">Partial measured totals — levels are never added together.</div>
        {report.reconciliation.allMatch ? (
          <div tw="text-[#86efac]">reconciliation: category rows = totals</div>
        ) : (
          <div tw="text-[#fca5a5]">reconciliation: MISMATCH (see exported JSON)</div>
        )}
      </div>

      <table tw="mb-2 w-full">
        <thead>
          <tr tw="text-left text-white/60">
            <th>category</th>
            <th>lvl</th>
            <th>↑</th>
            <th>↓</th>
            <th>n</th>
            <th>↓Mbps</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr
              key={`${row.level}|${row.category}|${row.source}`}
              // Breakdown rows are part of the row above; dimmed so they are not read as extra.
              style={row.partOf ? { opacity: 0.55 } : undefined}
            >
              <td tw="max-w-[150px] truncate pr-1" title={row.limitations}>
                {row.partOf ? '  └ ' : ''}
                {row.category}
              </td>
              <td>{LEVEL_SHORT[row.level] ?? row.level}</td>
              <td>{formatBytes(row.uploadBytes)}</td>
              <td>{formatBytes(row.downloadBytes)}</td>
              <td>{row.count}</td>
              <td>{formatMbps(row.averageDownloadMbps)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {report.stageRows.length > 0 && (
        <table tw="mb-2 w-full">
          <thead>
            <tr tw="text-left text-white/60">
              <th>ws stage</th>
              <th>s</th>
              <th>↑ msg/s</th>
              <th>↓ msg/s</th>
              <th>↑</th>
              <th>↓</th>
            </tr>
          </thead>
          <tbody>
            {report.stageRows.map(row => (
              <tr key={`${row.category}|${row.stage}`}>
                <td tw="max-w-[140px] truncate pr-1">{row.stage}</td>
                <td>{row.stageSeconds ?? 'n/a'}</td>
                <td>{row.sentMessagesPerSecond ?? 'n/a'}</td>
                <td>{row.receivedMessagesPerSecond ?? 'n/a'}</td>
                <td>{formatBytes(row.sentBytes)}</td>
                <td>{formatBytes(row.receivedBytes)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <details>
        <summary tw="cursor-pointer text-white/70">Coverage gaps ({gaps.length})</summary>
        <ul tw="mt-1 list-disc pl-4">
          {gaps.map(item => (
            <li key={item.source}>
              <span tw="text-[#fde68a]">{item.status}</span> — {item.source}
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}
